import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import './Dashboard.css';

import API_URL from '../../config';
import { getUserGradient as getGradient } from '../../utils/userGradient';

const SECTOR_LABELS = {
  engineer_freelance: 'Serbest Muellif',
  engineer_bureau: 'Buro Calisani',
  inspector: 'Yapi Denetim',
  authority: 'Belediye',
  student: 'Ogrenci',
  supplier: 'Tedarikci',
  employer: 'Isveren'
};

const LEVEL_LABELS = {
  acemi: 'Acemi',
  kalfa: 'Kalfa',
  usta: 'Usta',
  master: 'Master'
};

const UserCard = ({ u, apiUrl, sectorLabels, levelLabels, onClick, actionBtn }) => {
  const gradient = getGradient(u.id);
  return (
    <div className="agim-card" onClick={onClick}>
      <div className="agim-card-header" style={{
        background: `linear-gradient(135deg, ${gradient[0]}30, ${gradient[1]}18, ${gradient[2]}10)`
      }}>
        <svg className="agim-card-wave" viewBox="0 0 400 200" preserveAspectRatio="none">
          <defs>
            <linearGradient id={`wg1-${u.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={gradient[0]} stopOpacity="0.35" />
              <stop offset="100%" stopColor={gradient[1]} stopOpacity="0.08" />
            </linearGradient>
            <linearGradient id={`wg2-${u.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={gradient[1]} stopOpacity="0.08" />
              <stop offset="100%" stopColor={gradient[2]} stopOpacity="0.30" />
            </linearGradient>
          </defs>
          <path d="M0,0 C80,40 150,100 200,100 C250,100 320,40 400,200 L400,200 L0,200 Z" fill={`url(#wg1-${u.id})`} />
          <path d="M0,0 L0,0 C80,60 160,120 220,110 C280,100 350,160 400,200 L400,0 Z" fill={`url(#wg2-${u.id})`} />
        </svg>
      </div>
      <div className="agim-card-avatar-wrap">
        <div className="agim-card-avatar" style={{
          background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[2]})`
        }}>
          {u.avatar ? <img src={apiUrl + u.avatar} alt="" /> : (u.firstName?.[0] || '') + (u.lastName?.[0] || '')}
        </div>
        {u.level && (
          <span className="agim-card-level" style={{ background: `${gradient[0]}22`, color: gradient[0] }}>
            {levelLabels[u.level] || u.level}
          </span>
        )}
      </div>
      <div className="agim-card-body">
        <span className="agim-card-name">{u.firstName} {u.lastName}</span>
        {u.sectorRole && <span className="agim-card-role" style={{ background: `${gradient[0]}18`, color: gradient[0] }}>{sectorLabels[u.sectorRole]}</span>}
        <div className="agim-card-info-row">
          <span className="agim-card-level-tag" style={{ background: `${gradient[1]}18`, color: gradient[1] }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            {levelLabels[u.level] || 'Acemi'}
          </span>
          <span className="agim-card-xp-tag">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            <strong>{u.xp || 0}</strong> XP
          </span>
        </div>
        {u.bio && <span className="agim-card-bio">{u.bio}</span>}
        {u.city && (
          <span className="agim-card-city">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {u.city}
          </span>
        )}
        <div className="agim-card-stats">
          <span className="agim-card-stat">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <strong>{u.followerCount ?? 0}</strong> takipçi
          </span>
          <span className="agim-card-stat">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
            <strong>{u.postCount ?? 0}</strong> gonderi
          </span>
        </div>
      </div>
      {actionBtn}
    </div>
  );
};

const ORG_TYPE_LABELS = {
  engineering_bureau: 'Muhendislik Burosu',
  construction_company: 'Insaat Sirketi',
  inspection_firm: 'Denetim Firmasi',
  municipality: 'Belediye',
  fire_department: 'Itfaiye',
  osb: 'OSB',
  university: 'Universite',
  chamber: 'Meslek Odasi',
  supplier_company: 'Tedarikci',
  energy_company: 'Enerji Dagitim',
  other: 'Diger'
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const token = localStorage.getItem('projabze_token');

  // Filters
  const [searchQ, setSearchQ] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterTab, setFilterTab] = useState('all'); // all | people | orgs

  // Results
  const [people, setPeople] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) setUser(JSON.parse(stored));
    else navigate('/');
  }, [navigate]);

  // Fetch following + suggestions on mount
  useEffect(() => {
    if (!token || !user) return;
    (async () => {
      try {
        const [sugRes, folRes, frRes] = await Promise.all([
          fetch(`${API_URL}/api/social/users/suggestions`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/social/users/${user.id}/following`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/social/users/${user.id}/followers`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const sugData = await sugRes.json();
        setSuggestions(sugData.suggestions || []);
        const folData = await folRes.json();
        setFollowing(folData.following || []);
        const frData = await frRes.json();
        setFollowers(frData.followers || []);
      } catch { /* ignore */ }
    })();
  }, [token, user]);

  // Search
  const doSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQ) params.set('q', searchQ);
      if (filterCity) params.set('city', filterCity);

      const fetches = [];

      if (filterTab !== 'orgs') {
        fetches.push(
          fetch(`${API_URL}/api/social/users/search?${params}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => r.json()).then(d => d.users || [])
        );
      } else {
        fetches.push(Promise.resolve([]));
      }

      if (filterTab !== 'people') {
        const orgParams = new URLSearchParams();
        if (searchQ) orgParams.set('q', searchQ);
        fetches.push(
          fetch(`${API_URL}/api/organizations/search?${orgParams}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => r.json()).then(d => d.organizations || [])
        );
      } else {
        fetches.push(Promise.resolve([]));
      }

      const [ppl, org] = await Promise.all(fetches);

      let filteredPpl = ppl;
      if (filterRole) filteredPpl = ppl.filter(u => u.sectorRole === filterRole);

      setPeople(filteredPpl);
      setOrgs(org);
    } catch (err) {
      console.error('Search error:', err);
    }
    setLoading(false);
  }, [searchQ, filterRole, filterCity, filterTab, token]);

  useEffect(() => {
    if (token) doSearch();
  }, [filterTab, filterRole]);

  const handleFollow = async (targetId) => {
    try {
      await fetch(`${API_URL}/api/social/follows`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ followingId: targetId })
      });
      setPeople(prev => prev.map(u => u.id === targetId ? { ...u, isFollowing: true } : u));
      const followed = suggestions.find(s => s.id === targetId) || people.find(u => u.id === targetId);
      setSuggestions(prev => prev.filter(s => s.id !== targetId));
      if (followed) {
        setFollowing(prev => [...prev, { ...followed, isFollowing: true }]);
      }
    } catch { /* ignore */ }
  };

  const handleLogout = () => {
    localStorage.removeItem('projabze_token');
    localStorage.removeItem('projabze_user');
    navigate('/');
  };

  if (!user) return null;

  const showPeople = filterTab === 'all' || filterTab === 'people';
  const showOrgs = filterTab === 'all' || filterTab === 'orgs';

  return (
    <div className={`agim-page ${theme}`}>
      <Topbar user={user} onLogout={handleLogout} />
      <Sidebar activeId="feed" user={user} onLogout={handleLogout} />

      <main className="agim-main">
        <h1 className="agim-title">Ağım</h1>
        <p className="agim-subtitle">Kişi ve kurumları keşfet, bağlantını genişlet</p>

        {/* Filters */}
        <div className="agim-filters">
          <div className="agim-search-row">
            <input
              className="agim-search"
              type="text"
              placeholder="İsim, e-posta veya kurum ara..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
            />
            <button className="agim-search-btn" onClick={doSearch}>Ara</button>
          </div>
          <div className="agim-filter-row">
            <div className="agim-tabs">
              <button className={`agim-tab ${filterTab === 'all' ? 'active' : ''}`} onClick={() => setFilterTab('all')}>Tümü</button>
              <button className={`agim-tab ${filterTab === 'people' ? 'active' : ''}`} onClick={() => setFilterTab('people')}>Kişiler</button>
              <button className={`agim-tab ${filterTab === 'orgs' ? 'active' : ''}`} onClick={() => setFilterTab('orgs')}>Kurumlar</button>
            </div>
            {showPeople && (
              <select className="agim-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
                <option value="">Rol: Tümü</option>
                {Object.entries(SECTOR_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            )}
          </div>
        </div>

        {loading && <div className="agim-loading">Aranıyor...</div>}

        {/* My network - following */}
        {!searchQ && !filterRole && following.length > 0 && showPeople && (
          <div className="agim-section">
            <h2 className="agim-section-title">Takip Ettiklerim ({following.length})</h2>
            <div className="agim-grid">
              {following.map(u => (
                <UserCard key={u.id} u={u} apiUrl={API_URL} sectorLabels={SECTOR_LABELS} levelLabels={LEVEL_LABELS}
                  onClick={() => navigate(`/profile/${u.id}`)} />
              ))}
            </div>
          </div>
        )}

        {/* My network - followers */}
        {!searchQ && !filterRole && followers.length > 0 && showPeople && (
          <div className="agim-section">
            <h2 className="agim-section-title">Takip Edenler ({followers.length})</h2>
            <div className="agim-grid">
              {followers.map(u => (
                <UserCard key={u.id} u={u} apiUrl={API_URL} sectorLabels={SECTOR_LABELS} levelLabels={LEVEL_LABELS}
                  onClick={() => navigate(`/profile/${u.id}`)} />
              ))}
            </div>
          </div>
        )}

        {/* Suggestions */}
        {!searchQ && !filterRole && suggestions.length > 0 && showPeople && (
          <div className="agim-section">
            <h2 className="agim-section-title">Önerilen Kişiler</h2>
            <div className="agim-grid">
              {suggestions.map(u => (
                <UserCard key={u.id} u={u} apiUrl={API_URL} sectorLabels={SECTOR_LABELS} levelLabels={LEVEL_LABELS}
                  onClick={() => navigate(`/profile/${u.id}`)}
                  actionBtn={<button className="agim-card-follow" onClick={e => { e.stopPropagation(); handleFollow(u.id); }}>Takip Et</button>} />
              ))}
            </div>
          </div>
        )}

        {/* People search results */}
        {showPeople && people.length > 0 && (
          <div className="agim-section">
            <h2 className="agim-section-title">Kişiler ({people.length})</h2>
            <div className="agim-grid">
              {people.map(u => (
                <UserCard key={u.id} u={u} apiUrl={API_URL} sectorLabels={SECTOR_LABELS} levelLabels={LEVEL_LABELS}
                  onClick={() => navigate(`/profile/${u.id}`)}
                  actionBtn={u.isFollowing
                    ? <span className="agim-card-following">Takip Ediliyor</span>
                    : <button className="agim-card-follow" onClick={e => { e.stopPropagation(); handleFollow(u.id); }}>Takip Et</button>
                  } />
              ))}
            </div>
          </div>
        )}

        {/* Orgs results */}
        {showOrgs && orgs.length > 0 && (
          <div className="agim-section">
            <h2 className="agim-section-title">Kurumlar ({orgs.length})</h2>
            <div className="agim-grid">
              {orgs.map(o => (
                <div key={o.id} className="agim-card org-card" onClick={() => navigate('/groups')}>
                  <div className="agim-card-header org-header"></div>
                  <div className="agim-card-avatar-wrap">
                    <div className="agim-card-avatar org-avatar">
                      {o.avatar ? <img src={API_URL + o.avatar} alt="" /> : o.name?.[0] || 'K'}
                    </div>
                  </div>
                  <div className="agim-card-body">
                    <span className="agim-card-name">{o.name}</span>
                    <span className="agim-card-role">{ORG_TYPE_LABELS[o.type] || o.type}</span>
                    {o.city && <span className="agim-card-city">{o.city}</span>}
                    <span className="agim-card-members">{o.memberCount || 0} uye</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && people.length === 0 && orgs.length === 0 && (searchQ || filterRole) && (
          <div className="agim-empty">
            <span className="agim-empty-icon">🔍</span>
            <h3>Sonuc bulunamadi</h3>
            <p>Farkli filtreler veya arama terimleri deneyin.</p>
          </div>
        )}
      </main>

      <RightSidebar />
    </div>
  );
};

export default Dashboard;
